/**
 * Chart-settings STORE + pure colour functions — Phase 6a-4 commit 10 of the
 * decomposition (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, A34
 * store half, v2.8/R24). Bodies moved VERBATIM from static/index.html.
 *
 * STAYS in the page (D/E/F categories): applySymbolSettings,
 * applyCanvasSettings, applyAllChartSettings, populateSettingsModalFromSaved,
 * read*FormAndApply, applyTheme — everything touching DOM, chart handles, or
 * orchestration.
 *
 * _volBarColors memo — SINGLE OWNER, whole lifecycle (H22): module-private
 * cache, lazily created on first read, reused per bar (incl. the per-tick
 * handleCandleUpdate path — alias-only call, no wrapper), reset ONLY via the
 * exported invalidateVolBarColors() — which the page calls at its one
 * historical invalidation point (applySymbolSettings: "settings changed —
 * recompute the pair"). Page owns WHEN, module owns HOW (the health/H25
 * pattern). Module-level mutable state is deliberate here: this is a
 * page-singleton chart module (backtest precedent), not a multi-instance
 * service. Volume colours are theme-INDEPENDENT (settings only), so no
 * theme-change invalidation exists or is needed (R24-verified).
 *
 * isLight — NEVER cached, NEVER captured (H8 as rewritten in v2.8): the
 * colour functions take it as a parameter; callers compute it at call time
 * from document.body's class. This module has no theme state to go stale.
 */

import { hexToRgba } from "../domain/format";

/** Exported like AUTH_TOKEN_KEY — the reset-defaults flow clears it page-side. */
export const CHART_SETTINGS_KEY = 'nikhilchart_chart_settings';

export const DEFAULT_CHART_SETTINGS = {
  symbol: {
    bodyUp: '#26C281', bodyDown: '#F0616A',
    borderVisible: true, borderUp: '#26C281', borderDown: '#F0616A',
    wickVisible: true, wickUp: '#26C281', wickDown: '#F0616A',
    volumeUpColor: '#26C281', volumeDownColor: '#F0616A', volumeOpacity: 30,
  },
  statusLine: {
    title: true, chartValues: true, change: true, volume: true,
    indicatorTitles: true, indicatorValues: true,
  },
  canvas: {
    bgGradient: false, bgColor: '#141A22', bgColor2: '#171A1E',
    gridVertVisible: true, gridVertColor: '#23282f', gridVertOpacity: 100,
    gridHorzVisible: true, gridHorzColor: '#23282f', gridHorzOpacity: 100,
    // crosshairStyle uses lightweight-charts LineStyle values. "Dashed"
    // is 3 (LargeDashed, 6px on/off): the library's style 2 dash pattern
    // is 2px on/off at 1px width — visually identical to Dotted (1px/1px),
    // which is why the dropdown maps Dashed -> 3.
    crosshairColor: '#787b86', crosshairStyle: 3, crosshairOpacity: 45,
    watermarkVisible: false, watermarkText: '', watermarkColor: '#787b86', watermarkOpacity: 10,
    scaleTextColor: '#787b86',
    sessionSeparatorsVisible: true,
  },
};

export interface ChartSettings {
  symbol: Record<string, any>;
  statusLine: Record<string, any>;
  canvas: Record<string, any>;
}

export function getChartSettings(): ChartSettings {
  try {
    const raw = localStorage.getItem(CHART_SETTINGS_KEY);
    if (!raw) return JSON.parse(JSON.stringify(DEFAULT_CHART_SETTINGS));
    const parsed = JSON.parse(raw);
    // 2026-07 palette migration: saved blobs pin whatever the defaults
    // were when the user first opened the settings dialog. If a saved
    // color IS an old default (user never customized it), lift it to the
    // new muted palette; genuinely custom picks pass through untouched.
    if (parsed.symbol) {
      const LEGACY: Record<string, string> = { '#26a69a': '#089981', '#ef5350': '#f23645' };
      for (const k of ['bodyUp', 'bodyDown', 'borderUp', 'borderDown', 'wickUp', 'wickDown']) {
        if (LEGACY[parsed.symbol[k]]) parsed.symbol[k] = LEGACY[parsed.symbol[k]];
      }
      // volumeColor (single) -> volumeUpColor/volumeDownColor (2026-07-21).
      // Old defaults get the new split defaults via the merge below; a
      // genuinely custom single color seeds BOTH directions.
      if (parsed.symbol.volumeColor !== undefined) {
        const vc = parsed.symbol.volumeColor;
        if (vc !== '#26a69a' && vc !== '#6b8695' && parsed.symbol.volumeUpColor === undefined) {
          parsed.symbol.volumeUpColor = vc;
          parsed.symbol.volumeDownColor = vc;
        }
        delete parsed.symbol.volumeColor;
      }
    }
    // Crosshair: solid + default color = the user never customized it —
    // lift to the new dashed default. A deliberate solid pick would have
    // come with a color change (the dialog shipped both together).
    if (parsed.canvas && parsed.canvas.crosshairStyle === 0 &&
        (parsed.canvas.crosshairColor === undefined || parsed.canvas.crosshairColor === '#787b86')) {
      parsed.canvas.crosshairStyle = 3;
    }
    // Style 2 ("Dashed" until 2026-07-21) renders indistinguishably from
    // Dotted at 1px — anyone who saved 2 wanted a real dash: lift to 3.
    if (parsed.canvas && parsed.canvas.crosshairStyle === 2) {
      parsed.canvas.crosshairStyle = 3;
    }
    return {
      symbol: { ...DEFAULT_CHART_SETTINGS.symbol, ...(parsed.symbol || {}) },
      statusLine: { ...DEFAULT_CHART_SETTINGS.statusLine, ...(parsed.statusLine || {}) },
      canvas: { ...DEFAULT_CHART_SETTINGS.canvas, ...(parsed.canvas || {}) },
    };
  } catch (e) {
    return JSON.parse(JSON.stringify(DEFAULT_CHART_SETTINGS));
  }
}

export function saveChartSettings(section: keyof ChartSettings, partial: Record<string, any>): ChartSettings {
  const settings = getChartSettings();
  settings[section] = { ...settings[section], ...partial };
  try {
    localStorage.setItem(CHART_SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save chart settings:', e);
  }
  return settings;
}

// Cached up/down rgba pair for volume bars — computed from settings once,
// invalidated on settings change. Every volume data-build site colors each
// bar through this (candle direction decides up vs down), so reading the
// localStorage settings blob per bar is avoided.
let _volBarColors: { up: string; down: string } | null = null;

export function volumeBarColors(): { up: string; down: string } {
  if (!_volBarColors) {
    const s = getChartSettings().symbol;
    const op = (s.volumeOpacity != null ? s.volumeOpacity : 30) / 100;
    _volBarColors = {
      up: hexToRgba(s.volumeUpColor, op),
      down: hexToRgba(s.volumeDownColor, op),
    };
  }
  return _volBarColors;
}

export function volumeBarColor(open: number | null | undefined, close: number | null | undefined): string {
  const c = volumeBarColors();
  return (close != null && open != null && close < open) ? c.down : c.up;
}

/** The page's single historical invalidation point (applySymbolSettings's
 * `_volBarColors = null; // settings changed — recompute the pair`) calls
 * this — the ONLY way to reset the memo. */
export function invalidateVolBarColors(): void {
  _volBarColors = null;
}

// Theme-aware DEFAULT candle hues (light-mode fix). A candle color that is one
// of the recognized NEXUS defaults follows the current theme; ANY other value —
// a genuine custom pick, or a legacy #089981/#f23645 — is the user's and is
// returned untouched ("saved settings win"). This never rewrites a value that
// isn't already an exact NEXUS default, so it can't clobber user data.
export const CANDLE_THEME_HUES = {
  up:   { dark: '#26C281', light: '#0E9F6E' },
  down: { dark: '#F0616A', light: '#E02D3C' },
};

export function themedCandleColor(saved: string, isLight: boolean, dir: 'up' | 'down'): string {
  const h = CANDLE_THEME_HUES[dir];
  if (!h || !saved) return saved;
  const s = String(saved).toUpperCase();
  if (s === h.dark.toUpperCase() || s === h.light.toUpperCase()) return isLight ? h.light : h.dark;
  return saved;
}

/** The six themed candle colors for the current theme, resolved from settings. */
export function themedCandleOptions(symbolSettings: Record<string, any>, isLight: boolean) {
  const s = symbolSettings;
  return {
    upColor: themedCandleColor(s.bodyUp, isLight, 'up'),
    downColor: themedCandleColor(s.bodyDown, isLight, 'down'),
    borderUpColor: themedCandleColor(s.borderUp, isLight, 'up'),
    borderDownColor: themedCandleColor(s.borderDown, isLight, 'down'),
    wickUpColor: themedCandleColor(s.wickUp, isLight, 'up'),
    wickDownColor: themedCandleColor(s.wickDown, isLight, 'down'),
  };
}
